import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, AppAssets } from '../contexts/AuthContext';
import { User, UserRole, LoginHistoryLog, SessionHistoryLog } from '../types';
import { useFlight } from '../contexts/FlightContext';
import { updateUserRole } from '../services/userService';
import { clearReportsHistory, clearLoginHistory, clearNewSessionHistory } from '../services/systemService';
import { EyeIcon, EyeSlashIcon, CheckIcon, XCircleIcon, TrashIcon, UserPlusIcon, PencilIcon, SpinnerIcon, SpeakerWaveIcon, ArrowUpIcon } from '../components/Icons';
import { database } from '../services/firebase';
import { ref, onValue, set } from 'firebase/database';
import NewsInputField from '../components/NewsInputField';
import PwaIconWizard from '../components/PwaIconWizard';

// #region Sub-components for Admin Screen

const AssetUploader: React.FC<{
  assetKey: keyof AppAssets;
  assetUrl?: string;
  updateAsset: (assetKey: keyof AppAssets, dataUrl: string | null) => Promise<void>;
  assetType: 'image' | 'audio';
  label: string;
  showNotification: (message: string, type?: 'success' | 'error') => void;
  onOpenWizard: (assetKey: keyof AppAssets) => void;
}> = React.memo(({ assetKey, assetUrl, updateAsset, assetType, label, showNotification, onOpenWizard }) => {
    const { defaultAppAssets } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                await updateAsset(assetKey, e.target?.result as string);
                showNotification(`${label} updated successfully.`, 'success');
            } catch (err) {
                showNotification(`Failed to update ${label}.`, 'error');
            } finally {
                setIsUploading(false);
            }
        };
        reader.onerror = () => {
            setIsUploading(false);
            showNotification('Failed to read file.', 'error');
        };
        reader.readAsDataURL(file);
    }, [assetKey, updateAsset, label, showNotification]);

    const handleRemove = useCallback(async () => {
        if (window.confirm(`Are you sure you want to remove the ${label}?`)) {
            try {
                await updateAsset(assetKey, null);
                showNotification(`${label} removed successfully.`, 'success');
            } catch (err) {
                 showNotification(`Failed to remove ${label}.`, 'error');
            }
        }
    }, [assetKey, updateAsset, label, showNotification]);
    
    const handleTestSound = useCallback(() => {
        const soundToPlay = assetUrl || defaultAppAssets.notificationSound;
        if (soundToPlay) {
            const audio = new Audio(soundToPlay);
            audio.play().then(() => {
                showNotification("Test notification sound played!", 'success');
            }).catch(e => {
                console.error("Error playing test sound:", e);
                showNotification("Could not play sound. Please interact with the page first.", 'error');
            });
        } else {
            showNotification("No notification sound configured to test.", 'error');
        }
    }, [assetUrl, defaultAppAssets.notificationSound, showNotification]);

    const handleTrigger = () => {
        if (assetType === 'image') {
            onOpenWizard(assetKey);
        } else {
            inputRef.current?.click();
        }
    };


    return (
        <div className="relative bg-slate-700/50 p-3 rounded-lg flex flex-col items-center text-center">
             {isUploading && (
                <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center rounded-lg z-10">
                    <SpinnerIcon className="w-10 h-10 text-white animate-spin-slow" />
                </div>
            )}
            <h4 className="text-md font-semibold text-white mb-2">{label}</h4>
            <div className="h-24 w-full flex items-center justify-center bg-slate-800 rounded mb-2">
                {assetUrl ? (
                    assetType === 'image' ? (
                        <img src={assetUrl} alt={label} className="max-h-full max-w-full object-contain p-1" />
                    ) : (
                        <audio controls src={assetUrl} className="w-full h-10">Your browser does not support the audio element.</audio>
                    )
                ) : (
                    <span className="text-slate-500 text-xs">No file uploaded</span>
                )}
            </div>
            {assetType !== 'image' && (
                <input
                    type="file"
                    accept={`${assetType}/*`}
                    ref={inputRef}
                    onChange={handleFileChange}
                    className="hidden"
                />
            )}
            <div className="flex items-center gap-2 mt-auto w-full">
                <button
                    onClick={handleTrigger}
                    disabled={isUploading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded-lg text-xs disabled:bg-slate-500"
                >
                    {isUploading ? 'Uploading...' : (assetType === 'image' ? 'Wizard...' : 'Change')}
                </button>
                 {assetType === 'audio' && (
                    <button
                        onClick={handleTestSound}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-3 rounded-lg text-xs flex items-center justify-center gap-1"
                        title="Play notification sound"
                    >
                        <SpeakerWaveIcon className="w-4 h-4" />
                        Test
                    </button>
                )}
                {assetUrl && (
                    <button
                        onClick={handleRemove}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-lg text-xs"
                    >
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
});

const UserManagementPanel: React.FC<{
    users: User[];
    currentUser: User | null;
    isSuperAdmin: boolean;
    onShowCreateUser: () => void;
}> = ({ users, currentUser, isSuperAdmin, onShowCreateUser }) => {
    // Component logic remains large, but now isolated. For this request, keeping it here.
    const { showNotification } = useFlight();
    const { deleteUser, updateUserPassword } = useAuth();
    const [passwordVisibility, setPasswordVisibility] = useState<{ [key: string]: boolean }>({});
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const togglePasswordVisibility = (userId: string) => {
        setPasswordVisibility(prev => ({ ...prev, [userId]: !prev[userId] }));
    };

    const handleRoleChange = async (userId: string, newRole: UserRole) => {
        await updateUserRole(userId, newRole);
        showNotification("User role updated.", "success");
    };

    const handleUpdatePassword = async (userId: string) => {
        if (newPassword.length < 6) {
            showNotification('Password must be at least 6 characters long.', 'error');
            return;
        }
        await updateUserPassword(userId, newPassword);
        setEditingUserId(null);
        setNewPassword('');
        showNotification("Password updated successfully.", "success");
    };

    const handleDeleteUser = async (userId: string) => {
        await deleteUser(userId);
        showNotification("User deleted.", "success");
        setUserToDelete(null);
    };

    const filteredUsers = users.filter(u => {
      if (u.id === currentUser?.id) return false;
      if (!isSuperAdmin && u.username === 'JBP') return false;
      return true;
    });

    return (
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-blue-400">User Management</h2>
          <button onClick={onShowCreateUser} className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg text-sm">
            <UserPlusIcon className="w-5 h-5 mr-2"/>
            Create New User
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Password</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-slate-800/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{user.username}</div>
                        <div className="text-xs text-slate-400">{user.email || 'No email'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value as UserRole)}
                          className="py-1 px-2 text-white"
                          disabled={!isSuperAdmin && user.role === UserRole.ADMIN}
                        >
                          {Object.values(UserRole)
                            .filter(role => isSuperAdmin || role !== UserRole.ADMIN || role === user.role)
                            .map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                      </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                        {editingUserId === user.id ? (
                             <div className="flex items-center gap-2">
                                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-32" placeholder="New Password"/>
                                <button onClick={() => handleUpdatePassword(user.id)} className="text-green-400 hover:text-green-300"><CheckIcon className="w-5 h-5"/></button>
                                <button onClick={() => setEditingUserId(null)} className="text-red-400 hover:text-red-300"><XCircleIcon className="w-5 h-5"/></button>
                             </div>
                        ) : (
                            <div className="flex items-center gap-2">
                               <span>{passwordVisibility[user.id] ? user.password : '********'}</span>
                                {(isSuperAdmin || user.role !== UserRole.ADMIN) && (
                                  <>
                                    <button onClick={() => togglePasswordVisibility(user.id)} className="text-slate-400 hover:text-white">
                                        {passwordVisibility[user.id] ? <EyeSlashIcon className="w-5 h-5"/> : <EyeIcon className="w-5 h-5"/>}
                                    </button>
                                    <button onClick={() => { setEditingUserId(user.id); setNewPassword('');}} className="text-blue-400 hover:text-white">
                                      <PencilIcon className="w-4 h-4"/>
                                    </button>
                                  </>
                                )}
                            </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {(isSuperAdmin || user.role !== UserRole.ADMIN) &&
                            <button onClick={() => setUserToDelete(user)} className="text-red-500 hover:text-red-400">
                                <TrashIcon className="w-5 h-5"/>
                            </button>
                          }
                      </td>
                    </tr>
              ))}
            </tbody>
          </table>
        </div>
        {userToDelete && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                <h2 className="text-xl font-bold text-white mb-4">Confirm Deletion</h2>
                <p className="text-slate-300 mb-6">
                Are you sure you want to permanently delete user <span className="font-bold text-amber-400">{userToDelete.username}</span>? This action cannot be undone.
                </p>
                <div className="flex justify-center gap-4">
                <button onClick={() => setUserToDelete(null)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg transition-colors">Cancel</button>
                <button onClick={() => handleDeleteUser(userToDelete.id)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg transition-colors">Confirm Delete</button>
                </div>
            </div>
            </div>
        )}
      </div>
    );
};

// #endregion

const AdminScreen: React.FC = () => {
  const { user: currentUser, users, isRegistrationOpen, toggleRegistration, authCodes, updateAuthCodes, createUser, securityScanPercentage, updateSecurityScanPercentage, updateUserPassword, appAssets, updateAsset, vapidKey, updateVapidKey, isMultiFlightEnabled, toggleMultiFlight } = useAuth();
  const { clearNewsFeed, showNotification } = useFlight();
  
  const [flightNumbers, setFlightNumbers] = useState<string[]>([]);
  const [returnFlights, setReturnFlights] = useState<string[]>([]);
  const [newFlightNumber, setNewFlightNumber] = useState('');
  
  const [newAuthCode, setNewAuthCode] = useState('');
  const [selectedRoleForCode, setSelectedRoleForCode] = useState<UserRole>(UserRole.AGENT);

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', role: UserRole.AGENT });
  
  const [groupedLoginHistory, setGroupedLoginHistory] = useState<{ [date: string]: (LoginHistoryLog & { key: string })[] }>({});
  const [sortedHistoryDates, setSortedHistoryDates] = useState<string[]>([]);
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryLog[]>([]);
  
  const [newScanPercentage, setNewScanPercentage] = useState(securityScanPercentage);
  const [newVapidKey, setNewVapidKey] = useState(vapidKey);
  
  const [wizardState, setWizardState] = useState<{ isOpen: boolean; targetAssetKey?: keyof AppAssets }>({ isOpen: false, targetAssetKey: undefined });

  const handleOpenWizard = useCallback((assetKey: keyof AppAssets) => {
      setWizardState({ isOpen: true, targetAssetKey: assetKey });
  }, []);

  const isSuperAdmin = currentUser?.username === 'JBP';
  const isAdmin = currentUser?.role === UserRole.ADMIN;

  useEffect(() => {
    setNewScanPercentage(securityScanPercentage);
  }, [securityScanPercentage]);

  useEffect(() => {
    setNewVapidKey(vapidKey);
  }, [vapidKey]);

  useEffect(() => {
    const flightNumbersRef = ref(database, 'config/flightNumbers');
    const flightUnsubscribe = onValue(flightNumbersRef, (snapshot) => {
        setFlightNumbers(snapshot.val() || []);
    });

    const returnFlightsRef = ref(database, 'config/returnFlights');
    const returnFlightsUnsubscribe = onValue(returnFlightsRef, (snapshot) => {
        setReturnFlights(snapshot.val() || []);
    });
    
    const historyRef = ref(database, 'loginHistory');
    const historyUnsubscribe = onValue(historyRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
            const logs: (LoginHistoryLog & { key: string })[] = Object.entries(data).map(([key, value]) => ({
                key,
                ...((value as unknown) as LoginHistoryLog)
            })).filter(log => log.username && log.action && typeof log.timestamp === 'number');

            const groups: { [date: string]: typeof logs } = {};
            logs.forEach(log => {
                const date = new Date(log.timestamp).toISOString().split('T')[0];
                if (!groups[date]) {
                    groups[date] = [];
                }
                groups[date].push(log);
            });

            for (const date in groups) {
                groups[date].sort((a, b) => b.timestamp - a.timestamp);
            }
            
            setGroupedLoginHistory(groups);
            setSortedHistoryDates(Object.keys(groups).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()));
        } else {
            setGroupedLoginHistory({});
            setSortedHistoryDates([]);
        }
    });

    const sessionHistoryRef = ref(database, 'newSessionHistory');
    const sessionHistoryUnsubscribe = onValue(sessionHistoryRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
            const logs: SessionHistoryLog[] = Object.entries(data).map(([key, value]) => ({
                key,
                ...((value as any) as Omit<SessionHistoryLog, 'key'>)
            })).sort((a,b) => b.timestamp - a.timestamp);
            setSessionHistory(logs);
        } else {
            setSessionHistory([]);
        }
    });

    return () => {
        flightUnsubscribe();
        historyUnsubscribe();
        sessionHistoryUnsubscribe();
        returnFlightsUnsubscribe();
    };
  }, []);
  
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
        showNotification("Username and password are required.", 'error');
        return;
    }
    if (newUser.password.length < 6) {
        showNotification('Password must be at least 6 characters long.', 'error');
        return;
    }
    const result = await createUser(newUser);
    if (result.success) {
        showNotification(result.message, 'success');
        setNewUser({ username: '', password: '', email: '', role: UserRole.AGENT });
        setShowCreateUser(false);
    } else {
        showNotification(result.message, 'error');
    }
  };
  
  const addFlightNumber = async () => {
    if (newFlightNumber && !flightNumbers.includes(newFlightNumber)) {
        const updatedFlights = [...flightNumbers, newFlightNumber];
        try {
            await set(ref(database, 'config/flightNumbers'), updatedFlights);
            setNewFlightNumber('');
            showNotification('Flight number added successfully.');
        } catch(e) {
            showNotification('Failed to add flight number.', 'error');
            console.error(e);
        }
    }
  };

  const removeFlightNumber = async (flightToRemove: string) => {
    const updatedFlights = flightNumbers.filter(f => f !== flightToRemove);
    try {
        await set(ref(database, 'config/flightNumbers'), updatedFlights);
        showNotification('Flight number removed.');
    } catch(e) {
        showNotification('Failed to remove flight number.', 'error');
        console.error(e);
    }
  };

  const handleToggleReturnFlight = async (flightNo: string) => {
    const isCurrentlyReturn = returnFlights.includes(flightNo);
    const updatedReturnFlights = isCurrentlyReturn
        ? returnFlights.filter(f => f !== flightNo)
        : [...returnFlights, flightNo];
    
    try {
        await set(ref(database, 'config/returnFlights'), updatedReturnFlights);
        showNotification(`'${flightNo}' return status updated.`, 'success');
    } catch (e) {
        showNotification('Failed to update return status.', 'error');
        console.error(e);
    }
  };
  
  const handleAddCode = async () => {
    if (newAuthCode && !authCodes[newAuthCode]) {
        const newCodes = { ...authCodes, [newAuthCode]: selectedRoleForCode };
        try {
            await updateAuthCodes(newCodes);
            setNewAuthCode('');
            showNotification('Authorization code added.');
        } catch(e) {
            showNotification('Failed to add auth code.', 'error');
            console.error(e);
        }
    }
  };

  const handleRemoveCode = async (code: string) => {
    const newCodes = { ...authCodes };
    delete newCodes[code];
    try {
        await updateAuthCodes(newCodes);
        showNotification('Authorization code removed.');
    } catch(e) {
        showNotification('Failed to remove auth code.', 'error');
        console.error(e);
    }
  };

  const handleClearHistory = async (type: 'reports' | 'logins' | 'sessions') => {
    const confirmation = window.prompt(`This is a destructive action. To confirm, type "DELETE ${type.toUpperCase()}"`);
    if (confirmation === `DELETE ${type.toUpperCase()}`) {
      try {
        if (type === 'reports') await clearReportsHistory();
        if (type === 'logins') await clearLoginHistory();
        if (type === 'sessions') await clearNewSessionHistory();
        showNotification(`${type} history cleared successfully.`, 'success');
      } catch (error) {
        showNotification(`Failed to clear ${type} history.`, 'error');
      }
    } else {
        showNotification("Action cancelled.", 'error');
    }
  };

  const handleClearNews = () => {
    if (window.confirm("Are you sure you want to clear all announcements?")) {
        clearNewsFeed();
        showNotification("Announcements cleared.", "success");
    }
  };

  const handleUpdatePercentage = async () => {
    const percentage = Number(newScanPercentage);
    if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
        try {
            await updateSecurityScanPercentage(percentage);
            showNotification('Security scan percentage updated.', 'success');
        } catch (e) {
            showNotification('Failed to update percentage.', 'error');
            console.error(e);
        }
    } else {
        showNotification('Please enter a valid percentage (0-100).', 'error');
    }
  };

  const handleUpdateVapidKey = async () => {
    try {
        await updateVapidKey(newVapidKey);
        showNotification('VAPID Key updated successfully.', 'success');
    } catch (e) {
        showNotification('Failed to update VAPID Key.', 'error');
        console.error(e);
    }
  };

  const formatDateHeader = (dateStr: string): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const reportDate = new Date(dateStr);

    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    reportDate.setHours(0, 0, 0, 0);

    let prefix = '';
    if (reportDate.getTime() === today.getTime()) {
        prefix = 'Today - ';
    } else if (reportDate.getTime() === yesterday.getTime()) {
        prefix = 'Yesterday - ';
    }
    
    return prefix + new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
    });
  };

  return (
    <div className="space-y-6">
      <UserManagementPanel users={users} currentUser={currentUser} isSuperAdmin={isSuperAdmin} onShowCreateUser={() => setShowCreateUser(true)} />

      {isSuperAdmin && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h2 className="text-xl font-bold mb-4 text-blue-400">App Dependencies</h2>
          <p className="text-sm text-slate-400 mb-4 -mt-2">Use the wizard to upload one image and update both the specific asset and all PWA icons simultaneously.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <AssetUploader assetKey="logo1" assetUrl={appAssets.logo1} updateAsset={updateAsset} assetType="image" label="Primary Logo" showNotification={showNotification} onOpenWizard={handleOpenWizard}/>
            <AssetUploader assetKey="logo2" assetUrl={appAssets.logo2} updateAsset={updateAsset} assetType="image" label="Secondary Logo" showNotification={showNotification} onOpenWizard={handleOpenWizard}/>
            <AssetUploader assetKey="crewIcon" assetUrl={appAssets.crewIcon} updateAsset={updateAsset} assetType="image" label="Crew Icon" showNotification={showNotification} onOpenWizard={handleOpenWizard}/>
            <AssetUploader assetKey="pilotIcon" assetUrl={appAssets.pilotIcon} updateAsset={updateAsset} assetType="image" label="Pilot Icon" showNotification={showNotification} onOpenWizard={handleOpenWizard}/>
            <AssetUploader assetKey="passengersIcon" assetUrl={appAssets.passengersIcon} updateAsset={updateAsset} assetType="image" label="Passengers Icon" showNotification={showNotification} onOpenWizard={handleOpenWizard}/>
            <AssetUploader assetKey="notificationSound" assetUrl={appAssets.notificationSound} updateAsset={updateAsset} assetType="audio" label="Notification Sound" showNotification={showNotification} onOpenWizard={handleOpenWizard}/>
            <AssetUploader assetKey="notificationSound2" assetUrl={appAssets.notificationSound2} updateAsset={updateAsset} assetType="audio" label="Arrival Closed Sound" showNotification={showNotification} onOpenWizard={handleOpenWizard}/>
          </div>
        </div>
      )}

      {isSuperAdmin && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h2 className="text-xl font-bold mb-4 text-blue-400">Flight Number Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-white mb-2">Add New Flight Number</h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newFlightNumber}
                  onChange={(e) => setNewFlightNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., JT004"
                  className="w-full"
                />
                <button onClick={addFlightNumber} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Add</button>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">Existing Flight Numbers</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {flightNumbers.map(fn => (
                  <div key={fn} className="flex items-center justify-between bg-slate-800/50 p-2 rounded-md">
                    <span className="font-mono text-white">{fn}</span>
                    <div className='flex items-center gap-2'>
                        <label className='text-xs flex items-center gap-1 cursor-pointer'>
                            <input 
                                type="checkbox" 
                                checked={returnFlights.includes(fn)} 
                                onChange={() => handleToggleReturnFlight(fn)}
                                className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-600 rounded focus:ring-purple-500"
                            />
                            Return
                        </label>
                        <button onClick={() => removeFlightNumber(fn)} className="text-red-500 hover:text-red-400">
                            <TrashIcon className="w-4 h-4"/>
                        </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {isAdmin && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h2 className="text-xl font-bold mb-4 text-blue-400">System Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <div>
              <h3 className="font-semibold text-white mb-2">Registration</h3>
              <div className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg">
                <span className="text-sm text-slate-300">Allow new users to register</span>
                <button onClick={toggleRegistration} className={`font-bold py-1 px-4 rounded-full text-xs transition-colors ${isRegistrationOpen ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                  {isRegistrationOpen ? 'OPEN' : 'CLOSED'}
                </button>
              </div>
              <div className="mt-4">
                <h4 className="font-semibold text-white mb-2">Authorization Codes</h4>
                <div className="flex items-stretch gap-2 mb-2">
                  <input type="text" value={newAuthCode} onChange={(e) => setNewAuthCode(e.target.value.toUpperCase())} placeholder="New Code" className="flex-grow"/>
                  <select value={selectedRoleForCode} onChange={(e) => setSelectedRoleForCode(e.target.value as UserRole)} className="flex-shrink">
                    {Object.values(UserRole).filter(r => r !== UserRole.ADMIN).map(role => <option key={role} value={role}>{role}</option>)}
                  </select>
                  <button onClick={handleAddCode} className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 rounded-lg text-sm">Add</button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {Object.entries(authCodes).map(([code, role]) => (
                    <div key={code} className="flex items-center justify-between bg-slate-800/50 p-2 rounded-md">
                      <span className="font-mono text-white">{code} <span className="text-xs text-slate-400">({role})</span></span>
                      <button onClick={() => handleRemoveCode(code)} className="text-red-500 hover:text-red-400"><TrashIcon className="w-4 h-4"/></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-2">General</h3>
              <div className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg mb-4">
                <span className="text-sm text-slate-300">Multi-Flight Mode</span>
                <button onClick={toggleMultiFlight} className={`font-bold py-1 px-4 rounded-full text-xs transition-colors ${isMultiFlightEnabled ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                  {isMultiFlightEnabled ? 'ENABLED' : 'DISABLED'}
                </button>
              </div>
              <div className="bg-slate-800/50 p-3 rounded-lg mb-4">
                <label className="block text-sm text-slate-300 mb-1">Security Scan Percentage</label>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="100" value={newScanPercentage} onChange={(e) => setNewScanPercentage(Number(e.target.value))} className="w-full"/>
                  <button onClick={handleUpdatePercentage} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg text-sm">Save</button>
                </div>
              </div>
              {isSuperAdmin && (
                 <div className="bg-slate-800/50 p-3 rounded-lg">
                    <label className="block text-sm text-slate-300 mb-1">VAPID Key for Push Notifications</label>
                    <div className="flex items-center gap-2">
                        <input type="text" value={newVapidKey} onChange={e => setNewVapidKey(e.target.value)} className="w-full text-xs"/>
                        <button onClick={handleUpdateVapidKey} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg text-sm">Save</button>
                    </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <NewsInputField />
      <div className="text-right">
        <button onClick={handleClearNews} className="text-sm text-red-400 hover:text-red-300">Clear All Announcements</button>
      </div>

      {isSuperAdmin && (
        <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
          <h2 className="text-xl font-bold mb-4 text-blue-400">History Logs</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="max-h-96 overflow-y-auto bg-slate-800/50 p-3 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                 <h3 className="font-semibold text-white">Login History</h3>
                 <button onClick={() => handleClearHistory('logins')} className="text-xs text-red-400 hover:text-red-300">Clear</button>
              </div>
              {sortedHistoryDates.map(date => (
                <div key={date}>
                  <h4 className="font-semibold text-amber-300 text-sm sticky top-0 bg-slate-800 py-1">{formatDateHeader(date)}</h4>
                  <ul className="text-xs space-y-1 mt-1">
                    {groupedLoginHistory[date].map(log => (
                        <li key={log.key} className="flex justify-between items-center">
                            <span>
                                <span className={log.action === 'login' ? 'text-green-400' : 'text-red-400'}>{log.username} {log.action}</span>
                                <span className="text-slate-400"> at {new Date(log.timestamp).toLocaleTimeString('en-GB')}</span>
                            </span>
                            <span className='text-slate-500 text-[10px]'>{log.city || '...'}</span>
                        </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
             <div className="max-h-96 overflow-y-auto bg-slate-800/50 p-3 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                 <h3 className="font-semibold text-white">New Session History</h3>
                 <button onClick={() => handleClearHistory('sessions')} className="text-xs text-red-400 hover:text-red-300">Clear</button>
              </div>
              <ul className="text-xs space-y-1 mt-1">
                 {sessionHistory.map(log => (
                    <li key={log.key} className="flex justify-between items-center">
                       <span>
                          <span className='text-green-400'>{log.username} started new session</span>
                          <span className="text-slate-400"> at {new Date(log.timestamp).toLocaleString('en-GB')}</span>
                       </span>
                    </li>
                 ))}
              </ul>
            </div>
          </div>
          <div className="text-center mt-4">
            <button onClick={() => handleClearHistory('reports')} className="text-sm text-red-400 hover:text-red-300">Clear All Flight Reports History</button>
          </div>
        </div>
      )}
      
      {showCreateUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md border border-slate-700">
                <h2 className="text-2xl font-bold text-white mb-4">Create New User</h2>
                <div className="space-y-4">
                    <input value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} placeholder="Username" className="w-full" />
                    <input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Password (min 6 chars)" className="w-full" />
                    <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="Email" className="w-full" />
                    <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value as UserRole})} className="w-full">
                        {Object.values(UserRole).filter(r => isSuperAdmin || r !== UserRole.ADMIN).map(role => <option key={role} value={role}>{role}</option>)}
                    </select>
                </div>
                 <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-slate-700">
                    <button onClick={() => setShowCreateUser(false)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                    <button onClick={handleCreateUser} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">Create</button>
                </div>
            </div>
        </div>
      )}

      <PwaIconWizard isOpen={wizardState.isOpen} onClose={() => setWizardState({ isOpen: false, targetAssetKey: undefined })} targetAssetKey={wizardState.targetAssetKey} />

    </div>
  );
};

export default AdminScreen;
